import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Scale, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { CiteRuling } from './CiteRuling';

const RES = { acoge: 'acoge', rechaza: 'confirma o rechaza', inadmisible: 'inadmisible', otro: 'otro' };
const TONE = { 'Suele acoger los recursos': 'text-emerald-300', 'Suele confirmar o rechazar': 'text-red-300', 'Resultados divididos': 'text-amber-300' };

const Block = ({ title, d, testid }) => (!d ? null : (
  <div data-testid={testid} className="text-[11px] text-slate-300">
    <p><b className="text-slate-200">{title}:</b> <span className={TONE[d.tendencia] || 'text-slate-400'}>{d.tendencia}</span> · {d.n} fallos revisados{d.total > d.n ? ` de ${d.total}` : ''}</p>
    {d.n > 0 && <p className="text-slate-400">{Object.entries(d.resultados).map(([k, v]) => `${RES[k] || k}: ${v}`).join(' · ')}</p>}
    {d.descriptores.length > 0 && <p className="text-slate-500">Temas frecuentes: {d.descriptores.map((x) => x.d).join(', ')}</p>}
  </div>
));

const SimilarRulings = ({ matter, numero, onUpdated }) => {
  const { api } = useAuth();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setR(await runAiJob(api, null, null, { jobId: (await api.get(`/crm/matters/${matter.id}/sala-profile/${numero}/similar`)).data.job_id, timeout: 180000 })); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || 'No se pudieron comparar los fallos'); } finally { setBusy(false); }
  };
  if (!r) return (
    <button type="button" data-testid={`sala-similar-load-${numero}`} onClick={load} disabled={busy} className="mt-1 flex items-center gap-1 text-[11px] text-amber-300 underline disabled:opacity-50">
      {busy && <Loader2 className="h-3 w-3 animate-spin" />}{busy ? 'Comparando con los hechos de la causa...' : 'Fallos parecidos a esta causa'}
    </button>
  );
  return (
    <div data-testid={`sala-similar-${numero}`} className="mt-1 space-y-1">
      <p className="text-[11px] font-semibold text-slate-200">Fallos más parecidos · {r.revisados} revisados</p>
      {r.fallos.length === 0 && <p className="text-[11px] text-slate-500">Ningún fallo reciente de la sala se parece realmente a los hechos de la causa.</p>}
      {r.fallos.map((f, i) => (
        <div key={i} data-testid={`sala-similar-${numero}-item-${i}`} className="rounded border border-slate-700 p-1.5 text-[11px]">
          <p className="text-slate-200">{f.url ? <a href={f.url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">Rol {f.rol}</a> : `Rol ${f.rol}`} · {f.fecha} · {f.recurso} · <b>{f.resultado}</b> <span className="text-slate-500">· similitud {f.similitud}</span></p>
          <p className="text-slate-400">{f.motivo}</p>
          {matter.legal_brief ? <CiteRuling matter={matter} numero={numero} fallo={f} testid={`sala-cite-${numero}-${i}`} onUpdated={onUpdated} /> : <p className="mt-1 text-[10px] text-slate-500">Redacta el escrito del caso para poder citar este fallo.</p>}
        </div>
      ))}
    </div>
  );
};

const Sala = ({ s, mine, materia, matter, onUpdated }) => (
  <div data-testid={`sala-profile-${s.numero}`} className={`rounded-lg border p-2 ${mine ? 'border-amber-500/60 bg-amber-500/5' : 'border-slate-700 bg-slate-900/60'}`}>
    <p className="text-xs font-semibold text-amber-200">{s.nombre}{mine ? ' · sala de esta causa' : ''}</p>
    {s.integrantes.length > 0 && <p className="mb-1 text-[11px] text-slate-400">Integrantes actuales: {s.integrantes.map((i) => `${i.name} (${i.cargo}${i.estilo ? `, estilo ${i.estilo}${i.perfil_presuntivo ? ' presuntivo' : ''}` : ''})`).join(' · ')}</p>}
    {!s.general ? <p className="text-[11px] text-slate-500">Sin fallos publicados identificables para esta sala.</p> : <>
      <Block title={`En materia ${materia}`} d={s.materia} testid={`sala-profile-${s.numero}-materia`} />
      <Block title="Todas las materias" d={s.general} testid={`sala-profile-${s.numero}-general`} />
      <SimilarRulings matter={matter} numero={s.numero} onUpdated={onUpdated} />
    </>}
  </div>
);

// How each sala of the case's court has ruled in the last 12 months, next to its current members; the lawyer marks the sala hearing the case
export const SalaProfilePanel = ({ matter, onUpdated }) => {
  const { api } = useAuth();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setP(await runAiJob(api, null, null, { jobId: (await api.get(`/crm/matters/${matter.id}/sala-profile`)).data.job_id, timeout: 240000 })); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || 'No se pudo armar el perfil de sala'); } finally { setBusy(false); }
  };
  const pick = async (n) => {
    try { await api.patch(`/crm/matters/${matter.id}`, { sala_numero: Number(n) }); setP({ ...p, sala_numero: Number(n) }); onUpdated?.(); }
    catch { toast.error('No se pudo guardar la sala'); }
  };
  const salas = p ? [...p.salas].sort((a, b) => (b.numero === p.sala_numero) - (a.numero === p.sala_numero)) : [];
  return (
    <div data-testid="sala-profile-panel" className="rounded-xl border border-slate-700 bg-slate-800/40 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-semibold text-white"><Scale className="h-4 w-4 text-amber-400" />Perfil de sala{p ? ` · ${p.court.name}` : ''}</p>
        {!p && <button type="button" data-testid="sala-profile-load" onClick={load} disabled={busy} className="flex items-center gap-1 rounded-full border border-amber-500/50 px-3 py-1 text-xs text-amber-200 disabled:opacity-50">
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}{busy ? 'Consultando fallos oficiales...' : 'Ver cómo fallan las salas'}</button>}
      </div>
      {p && <div className="mt-2 space-y-2">
        <label className="flex items-center gap-2 text-xs text-slate-300">Sala que verá la causa:
          <select data-testid="sala-profile-select" value={p.sala_numero || ''} onChange={(e) => pick(e.target.value)} className="rounded-lg border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-white">
            <option value="" disabled>Elegir</option>{p.salas.map((s) => <option key={s.numero} value={s.numero}>{s.nombre}</option>)}
          </select>
        </label>
        {salas.map((s) => <Sala key={s.numero} s={s} mine={s.numero === p.sala_numero} materia={p.matter_type} matter={matter} onUpdated={onUpdated} />)}
        <ul className="list-disc pl-4 text-[10px] text-slate-500">{p.limites.map((l, i) => <li key={i}>{l}</li>)}</ul>
        <p className="flex items-center gap-1 text-[10px] text-slate-500">Fuente: <a href={p.fuente} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-slate-400 hover:text-amber-300">juris.pjud.cl<ExternalLink className="h-2.5 w-2.5" /></a> · desde {p.desde} · actualizado {String(p.actualizado).slice(0, 10)}</p>
      </div>}
    </div>
  );
};
